/**
 * TimeField — a time of day built from three explicit selects (hour, minute,
 * AM/PM) instead of a native `<input type="time">`.
 *
 * Why not the native control: the app had two time-entry patterns and two clock
 * conventions a few lines apart in Settings — select-based 12-hour times for the
 * daily-log and weigh-in reminders, and a native 24-hour input with a browser
 * clock icon for the medication reminder. Blood glucose and Sub-Q fluids used the
 * native one too. Native time inputs also render inconsistently across mobile
 * browsers (wheel picker, 12-hour text field, cramped touch targets), which is
 * why `TimePickerDropdown` already avoided them for reminder times.
 *
 * Why not just reuse `TimePickerDropdown` here: that control offers a 30-minute
 * grid, which is right for "when should we nudge you" and wrong for "when was
 * this reading taken" — rounding the minute of a glucose reading loses data. So
 * this one keeps every minute selectable, and shares the other control's 12-hour
 * clock so the app speaks one convention throughout.
 *
 * Values are read and emitted in 24-hour "HH:MM", exactly as the native input was.
 *
 * The clock shown follows the locale (finding 22, 9 Oct 2026): en-US keeps the
 * hour / minute / AM-PM selects; every other locale gets two selects, hour 00–23
 * and minute — see `lib/time.ts`.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../lib/format';
import { uses12HourClock } from '../../lib/time';

export interface TimeFieldProps {
  /** "HH:MM" (24-hour), or '' for empty. */
  value: string;
  /** Receives "HH:MM" (24-hour), or '' while any part is unselected. */
  onChange: (value: string) => void;
  /** Used to build each select's accessible name, e.g. "Time hour". */
  label?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  /** 12-hour selects with AM/PM. Defaults from the browser locale (en-US only). */
  twelveHour?: boolean;
}

type Period = 'AM' | 'PM';

interface Parts {
  hour12: string; // '1'–'12'
  minute: string; // '00'–'59'
  period: Period | '';
}

const EMPTY_PARTS: Parts = { hour12: '', minute: '', period: '' };

/** Split 24-hour "HH:MM" into 12-hour parts. */
function parseParts12(value: string | null | undefined): Parts {
  const match = /^(\d{2}):(\d{2})$/.exec((value ?? '').slice(0, 5));
  if (!match) return EMPTY_PARTS;
  const hour24 = Number(match[1]);
  const minute = match[2];
  if (hour24 > 23 || Number(minute) > 59) return EMPTY_PARTS;
  const period: Period = hour24 < 12 ? 'AM' : 'PM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { hour12: String(hour12), minute, period };
}

/** Assemble the three parts into 24-hour "HH:MM", or '' if incomplete. */
function compose12(parts: Parts): string {
  const { hour12, minute, period } = parts;
  if (!hour12 || !minute || !period) return '';
  const h = Number(hour12) % 12;
  const hour24 = period === 'PM' ? h + 12 : h;
  return `${String(hour24).padStart(2, '0')}:${minute}`;
}

/** On a 24-hour clock `hour12` holds the hour '00'–'23' and `period` is unused. */
function parseParts24(value: string | null | undefined): Parts {
  const match = /^(\d{2}):(\d{2})$/.exec((value ?? '').slice(0, 5));
  if (!match) return EMPTY_PARTS;
  if (Number(match[1]) > 23 || Number(match[2]) > 59) return EMPTY_PARTS;
  return { hour12: match[1], minute: match[2], period: '' };
}

function compose24(parts: Parts): string {
  return parts.hour12 && parts.minute ? `${parts.hour12}:${parts.minute}` : '';
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const HOURS_24 = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

export const TimeField: React.FC<TimeFieldProps> = ({
  value,
  onChange,
  label: labelProp,
  id,
  disabled = false,
  className,
  twelveHour = uses12HourClock(),
}) => {
  const { t } = useTranslation();
  const label = labelProp ?? t('shared.timeField.defaultLabel');
  const parseParts = twelveHour ? parseParts12 : parseParts24;
  const compose = twelveHour ? compose12 : compose24;
  const [parts, setParts] = useState<Parts>(() => parseParts(value));

  // Keep the selects in sync when the external value diverges from what they
  // already represent — comparing against the COMPOSED value, not the raw prop,
  // so a half-made selection (which composes to '' and pushes '' up) survives a
  // controlled parent echoing that '' back. Same reasoning as DateField.
  useEffect(() => {
    setParts((prev) => (compose(prev) === (value ?? '') ? prev : parseParts(value)));
  }, [value]);

  const commit = (next: Parts): void => {
    setParts(next);
    onChange(compose(next));
  };

  const selectClass = `min-w-0 px-3 py-2.5 text-sm rounded-xl border border-faint bg-white
    transition-all duration-200 focus:outline-none focus:border-sage-500 focus:shadow-glow
    disabled:bg-sage-50 disabled:text-gray-600 disabled:cursor-not-allowed min-h-[44px]`;

  return (
    <div className={`flex gap-2 ${className ?? ''}`} role="group" aria-label={label}>
      <select
        id={id}
        aria-label={t('shared.timeField.hourAria', { label })}
        className={`${selectClass} flex-1`}
        disabled={disabled}
        value={parts.hour12}
        onChange={(e) => commit({ ...parts, hour12: e.target.value })}
      >
        <option value="">{t('shared.timeField.hourPlaceholder')}</option>
        {twelveHour
          ? HOURS.map((h) => (
              <option key={h} value={String(h)}>
                {formatNumber(h)}
              </option>
            ))
          : HOURS_24.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
      </select>

      <select
        aria-label={t('shared.timeField.minuteAria', { label })}
        className={`${selectClass} flex-1`}
        disabled={disabled}
        value={parts.minute}
        onChange={(e) => commit({ ...parts, minute: e.target.value })}
      >
        <option value="">{t('shared.timeField.minutePlaceholder')}</option>
        {MINUTES.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>

      {twelveHour && (
        <select
          aria-label={t('shared.timeField.periodAria', { label })}
          className={`${selectClass} flex-1`}
          disabled={disabled}
          value={parts.period}
          onChange={(e) => commit({ ...parts, period: e.target.value as Period })}
        >
          <option value="">{t('shared.timeField.periodPlaceholder')}</option>
          <option value="AM">{t('time.am')}</option>
          <option value="PM">{t('time.pm')}</option>
        </select>
      )}
    </div>
  );
};

export default TimeField;
